import React from "react";
import { Eye, Users } from "lucide-react";
import defaultAvatar from "../../utils/profile.png";
import { getScoutContactAction } from "../../utils/scoutContactAction";

// Same sport palette used by PlayerCard, kept in sync for visual consistency.
const SPORT_COLORS = {
  Fútbol: "bg-green-100 text-green-700",
  Baloncesto: "bg-orange-100 text-orange-700",
  Tenis: "bg-yellow-100 text-yellow-700",
  Natación: "bg-cyan-100 text-cyan-700",
  Atletismo: "bg-purple-100 text-purple-700",
};

/**
 * Suggestion card for the scout dashboard's "Talentos Que Te Podrían
 * Interesar" carousel. Fixed width so a row of them scrolls horizontally on
 * mobile and lays out as a wrapped row on desktop. Fields come from
 * `v_scouting_athletes`.
 */
const TalentSuggestionCard = ({ player, contactRequest, onContact, onClick }) => {
  if (!player) return null;

  const {
    full_name = "Sin nombre",
    avatar_url = "",
    sport = "",
    position = "",
    category = "Amateur",
    country = "",
    age = 0,
    points = 0,
  } = player;

  const contactAction = getScoutContactAction(contactRequest);
  const sportColor = SPORT_COLORS[sport] || "bg-gray-100 text-gray-600";
  const subtitle = [category, country].filter(Boolean).join(" · ") || "Talento";

  return (
    <div
      onClick={onClick}
      className="flex w-[260px] flex-shrink-0 snap-start flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:w-[280px]"
    >
      <div className="flex items-center gap-3">
        <img
          src={avatar_url || defaultAvatar}
          alt={full_name}
          onError={(e) => {
            e.currentTarget.src = defaultAvatar;
          }}
          className="h-12 w-12 flex-shrink-0 rounded-full border-2 border-gray-100 object-cover"
        />
        <div className="min-w-0">
          <h4 className="truncate text-sm font-bold text-gray-900">
            {full_name}
          </h4>
          <p className="truncate text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {sport && (
          <span
            className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${sportColor}`}
          >
            {sport}
          </span>
        )}
        {position && (
          <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
            {position}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <Users size={13} /> {age || "—"}
          </span>
          <span className="inline-flex items-center gap-1">
            <Eye size={13} /> {points || "—"}
          </span>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onContact?.(player);
          }}
          disabled={contactAction.disabled}
          className="min-h-9 whitespace-nowrap rounded-md bg-blue-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-blue-100 disabled:text-blue-900"
        >
          {contactAction.label}
        </button>
      </div>
    </div>
  );
};

export default TalentSuggestionCard;
